/** @file /plays grid view: list, load, rename, delete. */
import { h, clear } from "./dom.js";
import { authStore } from "../core/auth-store.js";
import { listPlays, getPlay, renamePlay, deletePlay } from "../persistence/plays-repo.js";
import { playToState } from "../core/play-serializer.js";
import { appStore } from "../core/state.js";
import { navigate } from "../core/router.js";
import { historyReset } from "../core/history.js";
import { setCurrentPlay, currentPlayStore } from "../core/current-play.js";
import { openPromptDialog } from "./prompt-dialog.js";
import { openConfirmDialog } from "./confirm-dialog.js";
import { showToast } from "./toast.js";
import { mountShareDialog } from "./share-dialog.js";
import { DRAW_COLORS } from "../core/play.js";
import { resetEditorToNewPlay } from "../core/new-play.js";
import { markEditorClean } from "../core/editor-dirty.js";

/** @typedef {import("../types/schemas.js").PlayDoc} PlayDoc */

const rtf = new Intl.RelativeTimeFormat("de", { numeric: "auto" });

/** @param {Date} date @returns {string} */
function humanize(date) {
  const diffMs = date.getTime() - Date.now();
  const absHours = Math.abs(diffMs) / 3_600_000;
  if (absHours < 1) {
    return rtf.format(Math.round(diffMs / 60_000), "minute");
  }
  if (absHours < 24) {
    return rtf.format(Math.round(diffMs / 3_600_000), "hour");
  }
  return rtf.format(Math.round(diffMs / 86_400_000), "day");
}

/**
 * @param {HTMLElement} host
 */
export function mountPlaysLibraryView(host) {
  clear(host);
  const container = h("div", { class: "max-w-6xl mx-auto p-6" });
  host.appendChild(container);

  const header = h("div", { class: "flex items-center justify-between gap-3 mb-4 flex-wrap" });
  const title = h("h1", { class: "text-2xl font-bold" }, ["Meine Spielzüge"]);
  const newPlayBtn = h("button", { type: "button", class: "hb-btn hb-btn--primary min-h-11" }, [
    "+ Neuer Spielzug",
  ]);
  newPlayBtn.addEventListener("click", () => {
    resetEditorToNewPlay();
    navigate("editor");
  });
  header.appendChild(title);
  header.appendChild(newPlayBtn);
  container.appendChild(header);

  const grid = h("div", { class: "hb-plays-grid" });
  container.appendChild(grid);

  /** @type {"updatedAt" | "name"} */
  let sortMode = "updatedAt";
  /** @type {PlayDoc[]} */
  let currentPlays = [];

  const sortToggle = h(
    "button",
    { type: "button", class: "hb-btn min-h-11 mb-4" },
    ["Sortierung: Zuletzt bearbeitet"],
  );
  sortToggle.addEventListener("click", () => {
    sortMode = sortMode === "updatedAt" ? "name" : "updatedAt";
    sortToggle.textContent =
      sortMode === "updatedAt" ? "Sortierung: Zuletzt bearbeitet" : "Sortierung: Name A-Z";
    render(currentPlays);
  });
  container.insertBefore(sortToggle, grid);

  /** @param {PlayDoc[]} plays */
  function render(plays) {
    clear(grid);
    const sorted = [...plays].sort((a, b) => {
      if (sortMode === "name") {
        return a.name.localeCompare(b.name, "de");
      }
      return b.updatedAt.getTime() - a.updatedAt.getTime();
    });

    if (sorted.length === 0) {
      const empty = h("div", { class: "text-center py-16 text-neutral-400" }, [
        h("p", { class: "text-lg mb-4" }, ["Noch keine Spielzüge gespeichert."]),
      ]);
      const cta = h("button", { type: "button", class: "hb-btn hb-btn--primary min-h-11" }, [
        "Neuen Spielzug erstellen",
      ]);
      cta.addEventListener("click", () => {
        resetEditorToNewPlay();
        navigate("editor");
      });
      empty.appendChild(cta);
      grid.appendChild(empty);
      return;
    }

    for (const play of sorted) {
      grid.appendChild(makeCard(play, () => refresh()));
    }
  }

  async function refresh() {
    const uid = authStore.get().user?.uid;
    if (!uid) {
      return;
    }
    try {
      currentPlays = await listPlays(uid);
      render(currentPlays);
    } catch (err) {
      console.warn("[plays] list failed", err);
      showToast("Spielzüge konnten nicht geladen werden");
    }
  }

  grid.appendChild(h("div", { class: "text-neutral-400" }, ["Lade …"]));
  refresh();
}

/**
 * @param {PlayDoc} play
 * @param {() => void} onChanged
 * @returns {HTMLElement}
 */
function makeCard(play, onChanged) {
  const thumb = h("div", { class: "hb-play-card__thumb" });
  if (play.thumbnail) {
    thumb.style.backgroundImage = `url("${play.thumbnail}")`;
  }
  const title = h("div", { class: "font-semibold truncate" }, [play.name]);
  const meta = h("div", { class: "text-xs text-neutral-400" }, [humanize(play.updatedAt)]);

  const openBtn = h("button", { type: "button", class: "hb-btn min-h-11" }, ["Öffnen"]);
  const shareBtn = h(
    "button",
    { type: "button", class: "hb-btn min-h-11 min-w-11", "aria-label": "Teilen" },
    ["Teilen"],
  );
  const renameBtn = h("button", { type: "button", class: "hb-btn min-h-11" }, ["Umbenennen"]);
  const deleteBtn = h("button", { type: "button", class: "hb-btn hb-btn--danger min-h-11" }, [
    "Löschen",
  ]);

  const card = h("div", { class: "hb-play-card" }, [
    thumb,
    h("div", { class: "hb-play-card__body" }, [title, meta]),
    h("div", { class: "hb-play-card__actions" }, [openBtn, shareBtn, renameBtn, deleteBtn]),
  ]);

  shareBtn.addEventListener("click", () => {
    const uid = authStore.get().user?.uid;
    if (!uid) {
      return;
    }
    void mountShareDialog({ ownerId: uid, playId: play.id, playName: play.name });
  });

  openBtn.addEventListener("click", async () => {
    const uid = authStore.get().user?.uid;
    if (!uid) {
      return;
    }
    try {
      const doc = await getPlay(uid, play.id);
      if (!doc) {
        showToast("Spielzug nicht gefunden");
        return;
      }
      const { tokens, drawings, scenes, activeSceneIndex } = playToState(doc);
      appStore.set({
        tokens,
        drawings,
        scenes,
        activeSceneIndex,
        selectedDrawingId: null,
        tool: "select",
        drawColor: DRAW_COLORS[0],
      });
      historyReset();
      setCurrentPlay({
        playId: doc.id,
        name: doc.name,
        teamId: doc.teamId ?? null,
      });
      markEditorClean();
      navigate("editor");
    } catch (err) {
      console.warn("[plays] open failed", err);
      showToast("Laden fehlgeschlagen");
    }
  });

  renameBtn.addEventListener("click", async () => {
    const uid = authStore.get().user?.uid;
    if (!uid) {
      return;
    }
    const next = await openPromptDialog({
      title: "Umbenennen",
      initial: play.name,
      maxLength: 80,
    });
    if (!next || next === play.name) {
      return;
    }
    try {
      await renamePlay(uid, play.id, next);
      showToast("Umbenannt");
      onChanged();
    } catch (err) {
      console.warn("[plays] rename failed", err);
      showToast("Umbenennen fehlgeschlagen");
    }
  });

  deleteBtn.addEventListener("click", async () => {
    const uid = authStore.get().user?.uid;
    if (!uid) {
      return;
    }
    const ok = await openConfirmDialog({
      title: "Spielzug löschen?",
      body: play.name,
      destructive: true,
      confirmLabel: "Löschen",
    });
    if (!ok) {
      return;
    }
    try {
      await deletePlay(uid, play.id);
      const current = currentPlayStore.get().current;
      if (current?.playId === play.id) {
        resetEditorToNewPlay();
      }
      showToast("Gelöscht");
      onChanged();
    } catch (err) {
      console.warn("[plays] delete failed", err);
      showToast("Löschen fehlgeschlagen");
    }
  });

  return card;
}
